import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useOutletContext, useSearchParams } from "react-router-dom";
import "./GuitarTabs.css";

const CHORD_LINE_PATTERN = /^[A-G][#b]?(m|maj|min|sus|dim|aug|add|mmaj|maj7|m7|7|6|9|11|13|2|4|5|\/[A-G][#b]?|\(|\)|\+|-|no|\s)*$/i;

const isSectionHeader = (line) => /^\[[^\]]+\]$/.test(line.trim());

const isChordLine = (line) => {
  const trimmedLine = line.trim();
  if (!trimmedLine || isSectionHeader(trimmedLine)) {
    return false;
  }

  const tokens = trimmedLine.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return false;
  }

  return tokens.every((token) => CHORD_LINE_PATTERN.test(token));
};

const parseTabContent = (tabContent) => {
  const lines = tabContent.split("\n");
  return lines.map((line, index) => {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      return { type: "spacer", id: `spacer-${index}`, text: "" };
    }

    if (isSectionHeader(trimmedLine)) {
      return {
        type: "section",
        id: `section-${index}`,
        text: trimmedLine.replace(/^\[|\]$/g, ""),
      };
    }

    if (isChordLine(line)) {
      return {
        type: "chords",
        id: `chords-${index}`,
        text: line,
      };
    }

    return {
      type: "lyrics",
      id: `lyrics-${index}`,
      text: line,
    };
  });
};

const GuitarTabsView = () => {
  const { data } = useOutletContext();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const viewerRef = useRef(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState(2);
  const [fontSize, setFontSize] = useState(20);
  const [displayMode, setDisplayMode] = useState("formatted");
  const tabId = searchParams.get("tab");
  const selectedTab = data ? data.find((tab) => String(tab.id) === String(tabId)) : null;
  const successMessage = location.state && location.state.message ? location.state.message : "";
  const parsedTabContent = selectedTab ? parseTabContent(selectedTab.tabContent) : [];

  useEffect(() => {
    if (!isAutoScrolling) {
      return undefined;
    }

    const scrollInterval = window.setInterval(() => {
      window.scrollBy({
        top: scrollSpeed,
        left: 0,
        behavior: "auto",
      });
    }, 40);

    return () => window.clearInterval(scrollInterval);
  }, [isAutoScrolling, scrollSpeed]);

  useEffect(() => {
    setIsAutoScrolling(false);
  }, [selectedTab]);

  return (
    <div className="GuitarTabsPage">
      <section className="GuitarTabsPanel">
        <div className="GuitarTabsSectionHeader">
          <h2>Tab viewer</h2>
          <p>Use the library page to switch songs or come here directly from a saved tab.</p>
        </div>

        {successMessage ? <p className="GuitarTabsSuccess">{successMessage}</p> : null}

        {selectedTab ? (
          <div className="GuitarTabViewer">
            <div className="GuitarTabViewerHeader">
              <div>
                <h3>{selectedTab.songName}</h3>
                <p>{selectedTab.artist}</p>
              </div>
              <div className="GuitarTabMeta">
                <span>{selectedTab.tuning || "Standard"}</span>
                <span>{selectedTab.difficulty || "Unrated"}</span>
                <span>{selectedTab.createdAt}</span>
              </div>
            </div>

            <div className="GuitarTabsReaderControls">
              <div className="GuitarTabsControlGroup">
                <label htmlFor="scroll-speed">Scroll speed</label>
                <input
                  id="scroll-speed"
                  type="range"
                  min="1"
                  max="8"
                  value={scrollSpeed}
                  onChange={(event) => setScrollSpeed(Number(event.target.value))}
                />
                <span>{scrollSpeed}</span>
              </div>

              <div className="GuitarTabsControlGroup">
                <label htmlFor="font-size">Text size</label>
                <input
                  id="font-size"
                  type="range"
                  min="14"
                  max="32"
                  value={fontSize}
                  onChange={(event) => setFontSize(Number(event.target.value))}
                />
                <span>{fontSize}px</span>
              </div>

              <div className="GuitarTabsControlGroup">
                <label htmlFor="display-mode">Display mode</label>
                <select
                  id="display-mode"
                  value={displayMode}
                  onChange={(event) => setDisplayMode(event.target.value)}
                >
                  <option value="formatted">Formatted</option>
                  <option value="raw">Raw</option>
                </select>
              </div>

              <button
                className="GuitarTabsButton"
                type="button"
                onClick={() => setIsAutoScrolling((currentValue) => !currentValue)}
              >
                {isAutoScrolling ? "Stop Scroll" : "Start Scroll"}
              </button>
            </div>

            <div className="GuitarTabsButtonRow">
              <Link className="GuitarTabsSecondaryLink" to="/guitar-tabs">
                Back to Library
              </Link>
              <Link className="GuitarTabsSecondaryLink" to="/guitar-tabs/add">
                Add Another
              </Link>
            </div>

            {selectedTab.notes ? (
              <div className="GuitarTabNotes">
                <strong>Notes</strong>
                <p>{selectedTab.notes}</p>
              </div>
            ) : null}

            {displayMode === "formatted" ? (
              <div
                ref={viewerRef}
                className="GuitarTabFormattedContent"
                style={{ fontSize: `${fontSize}px` }}
              >
                {parsedTabContent.map((block) => (
                  <div
                    key={block.id}
                    className={`GuitarTabLine GuitarTabLine-${block.type}`}
                  >
                    {block.text}
                  </div>
                ))}
              </div>
            ) : (
              <pre
                ref={viewerRef}
                className="GuitarTabContent"
                style={{ fontSize: `${fontSize}px` }}
              >
                {selectedTab.tabContent}
              </pre>
            )}
          </div>
        ) : (
          <div className="GuitarTabsEmptyBlock">
            <p className="GuitarTabsEmptyState">Select a tab from the library to view it here.</p>
            <div className="GuitarTabsButtonRow">
              <Link className="GuitarTabsSecondaryLink" to="/guitar-tabs">
                Go to Library
              </Link>
              <Link className="GuitarTabsSecondaryLink" to="/guitar-tabs/add">
                Add New Tab
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default GuitarTabsView;
